import { isConsentCategoryEnabled, OneTrustCategories } from '@susu/cookie'

import { MARKETING_CLICK_ID_PARAMS, type MarketingClickIds } from './types'
import {
  getMarketingClickIdsFromCookies,
  getMarketingClickIdsFromUrl,
  getPersistedMarketingClickIds,
  persistMarketingClickIds,
} from './utils'

/** Click IDs captured from the landing URL — once per page load, not per read (O7). */
let capturedFromUrl: MarketingClickIds | undefined

/** Test hook: forget the landing capture so each test starts on a fresh page. */
export function resetMarketingClickIdsCapture(): void {
  capturedFromUrl = undefined
}

/**
 * Captures click IDs from the landing URL, once. The successful capture is
 * persisted and reused for every subsequent read instead of re-parsing
 * window.location per call. Consent is checked by the caller (O7).
 */
function captureMarketingClickIdsFromLanding(): MarketingClickIds {
  if (capturedFromUrl !== undefined) {
    return capturedFromUrl
  }
  capturedFromUrl = getMarketingClickIdsFromUrl(new URL(window.location.href))
  persistMarketingClickIds(capturedFromUrl)
  return capturedFromUrl
}

/**
 * Reads marketing click IDs in priority order: the landing-URL capture
 * (highest), then platform cookies, then the persisted `.suitsupply.com`
 * cookie (fixed 30-day expiry, see types.ts for the attribution note).
 * Everything is C0004-gated before any URL or storage read (O7) — the check
 * runs once here, before the landing capture, platform cookies, or persisted
 * storage are touched. A denial is not cached: consent granted later in the
 * session reads the (still current) URL/cookies/storage then.
 * SSR-safe: returns empty object when not in browser.
 */
export const getMarketingClickIds = (): MarketingClickIds => {
  if (typeof window === 'undefined') {
    return {}
  }
  if (!isConsentCategoryEnabled(OneTrustCategories.ADVERTISING)) {
    return {}
  }

  const fromUrl = captureMarketingClickIdsFromLanding()
  const fromStorage = getPersistedMarketingClickIds()
  const fromCookies = getMarketingClickIdsFromCookies()

  const result: MarketingClickIds = {}
  for (const param of MARKETING_CLICK_ID_PARAMS) {
    const value = fromUrl[param] ?? fromCookies[param] ?? fromStorage[param]
    if (value) {
      result[param] = value
    }
  }
  return result
}
